import React from 'react';
import { Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import UserStatePill from '../../../common/userStatePill/UserStatePill';
import {
    formatFunnelDetail,
    formatFunnelDuration,
    formatFunnelTimestamp,
    getFunnelRowStatus,
    getFunnelRowTone,
} from '../userFunnel.constants';
import styles from './styles/user-funnel-card.module.scss';

/**
 * One step of the onboarding timeline rendered inside `UserFunnelCard`:
 * marker on the rail, title + absolute timestamp on the headline row, the
 * backend's own sentence underneath, and a meta line carrying the status
 * pill, the elapsed gap, the journey span (only on the row that closes it)
 * and whatever `detail` the step came with.
 *
 * Everything visual is driven by the tone helper rather than the raw state,
 * so a failure, a data gap and a stage nobody reached stay distinguishable
 * at a glance (see `userFunnel.constants`).
 *
 * The error row is the one row that opens: it is a summary of a history the
 * timeline has no room for (see `UserFunnelAttemptsModal`), so when `onOpen`
 * is given it grows a "View attempts" button in its meta line. Every other row
 * is already showing everything it has, so making them clickable would promise
 * detail that does not exist.
 *
 * @param {Object} props
 * @param {Object} props.row Decorated milestone (`decorateFunnelMilestones`).
 * @param {boolean} [props.isCurrent] Row matches the payload's `current_stage`.
 * @param {boolean} [props.isLast] Last row — drops the trailing rail segment.
 * @param {(() => void) | null} [props.onOpen] Adds the button that opens the
 *   failure history. Absent = the row has nothing to open.
 */
function UserFunnelRow({ row, isCurrent = false, isLast = false, onOpen = null }) {
    const tone = getFunnelRowTone(row);
    const status = getFunnelRowStatus(row, { isCurrent });
    const timestamp = formatFunnelTimestamp(row?.atMs);
    const sinceLabel = formatFunnelDuration(row?.sinceMs);
    const totalLabel = formatFunnelDuration(row?.totalMs);
    const details = formatFunnelDetail(row?.detail);

    const rowClassName = [styles.row, styles[`row_${tone}`], isLast ? styles.rowLast : null].filter(Boolean).join(' ');

    return (
        <li className={rowClassName}>
            <span className={styles.marker} aria-hidden="true" />

            <div className={styles.body}>
                <div className={styles.headline}>
                    <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.title}>
                        {row?.title || '—'}
                    </Text>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.timestamp}>
                        {timestamp || '—'}
                    </Text>
                </div>

                {row?.description ? (
                    <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.description}>
                        {row.description}
                    </Text>
                ) : null}

                <div className={styles.meta}>
                    <UserStatePill tone={status.tone} label={status.label} />

                    {sinceLabel ? (
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.elapsed}>
                            {`+${sinceLabel}`}
                        </Text>
                    ) : null}

                    {totalLabel ? (
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.total}>
                            {`${totalLabel} total`}
                        </Text>
                    ) : null}

                    {details.map((detail) => (
                        <Text key={detail.key} as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.chip}>
                            {`${detail.label}: ${detail.value}`}
                        </Text>
                    ))}

                    {/* A real <button>, not the row body with role="button":
                        the button role makes its whole subtree presentational,
                        so wrapping the body would have hidden this row's title,
                        timestamp, description and chips from a screen reader —
                        on the one row whose description says what is blocking
                        the member. Keeping the control to itself leaves the row
                        readable and drops the hand-rolled key handling. */}
                    {onOpen ? (
                        <button type="button" className={styles.openBtn} onClick={onOpen}>
                            <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.openBtnLabel}>
                                View attempts
                            </Text>
                        </button>
                    ) : null}
                </div>
            </div>
        </li>
    );
}

export default UserFunnelRow;
